/**
 * Nueva pantalla de entrada:
 * - Selector de rol (Admin / Manager / Vendedor)
 * - Navega a /login?role=X (que ya no es bloqueado por el gate)
 * - Ya no pregunta la "contraseña de la tienda" — cada persona entra con
 *   su propio email + contraseña. El staff-login auto-desbloquea el gate.
 * - Muestra un ícono para instalar la app (PWA) cuando el navegador lo soporta.
 */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, User, Users, KeyRound, Download, Sparkles } from "lucide-react";

import { CanalesLogo } from "@/components/CanalesLogo";
import { Mascot } from "@/components/characters/Characters";

const ROLES = [
  {
    key: "seller",
    label_es: "Soy Vendedor",
    label_en: "I'm a Seller",
    hint_es: "Vendedor / Entregas / Personal de tienda",
    hint_en: "Seller / Delivery / Store staff",
    color: "bg-canales-yellow text-black",
    Icon: User,
    testid: "gate-role-seller",
  },
  {
    key: "manager",
    label_es: "Soy Manager",
    label_en: "I'm a Manager",
    hint_es: "Encargado / Licensee / Store Manager",
    hint_en: "Manager / Licensee",
    color: "bg-canales-blue text-white",
    Icon: Users,
    testid: "gate-role-manager",
  },
  {
    key: "admin",
    label_es: "Soy Admin",
    label_en: "I'm Admin",
    hint_es: "Corporativo / RH / Owner",
    hint_en: "Corporate / HR / Owner",
    color: "bg-canales-red text-white",
    Icon: ShieldCheck,
    testid: "gate-role-admin",
  },
];

export default function GateScreen() {
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative overflow-hidden"
      style={{ background: "#0d1a3f" }}
      data-testid="gate-screen"
    >
      {/* Background */}
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.25) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />
      <div
        className="absolute -left-24 top-32 w-[520px] h-[80px]"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }}
      />
      <div
        className="absolute -right-24 bottom-40 w-[420px] h-[60px]"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative brutal p-8 sm:p-10 bg-canales-cream w-full max-w-lg mx-4"
      >
        <div className="flex flex-col items-center text-center">
          <CanalesLogo width={180} />
          <div className="mt-3 inline-flex items-center gap-2 bg-canales-red text-white px-3 py-1 rounded-full border-2 border-black font-display font-bold text-xs uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> Acceso privado
          </div>
          <h1 className="mt-4 font-display font-black text-3xl">
            Bienvenido a Canales
          </h1>
          <p className="mt-1 text-sm font-body text-neutral-700">
            ¿Cómo entras hoy? Elige tu rol y usa tu email + contraseña.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3">
          {ROLES.map(({ key, label_es, hint_es, color, Icon, testid }) => (
            <Link
              key={key}
              to={`/login?role=${key}`}
              data-testid={testid}
              className={`brutal-btn ${color} p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform`}
            >
              <div className="w-12 h-12 rounded-2xl border-2 border-black bg-white/25 flex items-center justify-center shrink-0">
                <Icon className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="font-display font-black text-lg leading-tight">
                  {label_es}
                </div>
                <div className="text-xs font-body opacity-90 mt-0.5">
                  {hint_es}
                </div>
              </div>
              <KeyRound className="w-5 h-5 shrink-0 opacity-80" />
            </Link>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-center opacity-80">
          <Mascot size={64} />
        </div>
        <div className="text-center mt-2 text-xs font-body text-neutral-600">
          ¿Recibiste un link de examen o de invitación? Ábrelo directamente —
          no pide contraseña.
        </div>
      </motion.div>
    </div>
  );
}
